/**
 * AIAssistant — collapsible panel of AI helper chips shown in composers.
 *
 * Chips call POST /api/ai/assist with an intent + light context (topic,
 * category, current title/body). Results appear as clickable cards; the
 * caller decides how to apply the pick via the `on*` callbacks.
 *
 * Placement:
 *   • ChatPulseCompose (short thread / question)
 *   • ArticleWrite      (long-form article)
 *
 * Encourages people who "don't know what to post" to start typing.
 */
import { useCallback, useMemo, useState } from "react";
import {
  Sparkles, MessageSquarePlus, Wand2, HelpCircle, TagsIcon,
  Loader2, ChevronDown, ChevronUp, X, ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const INTENTS = [
  { key: "starter_prompts", label: "Starter prompts",   icon: Sparkles,          hint: "I don't know what to write yet" },
  { key: "suggest_titles",  label: "Suggest titles",    icon: MessageSquarePlus, hint: "Give me punchy title ideas" },
  { key: "make_question",   label: "Turn into question",icon: HelpCircle,        hint: "Rephrase as a community question" },
  { key: "improve_draft",   label: "Improve my draft",  icon: Wand2,             hint: "Polish grammar + clarity" },
  { key: "expand_draft",    label: "Expand my draft",   icon: ArrowRight,        hint: "Flesh out a thin start" },
  { key: "suggest_tags",    label: "Suggest tags",      icon: TagsIcon,          hint: "Tag ideas people will search" },
];

export default function AIAssistant({
  topic,
  category,
  location,
  currentTitle,
  currentBody,
  surface = "thread",
  language = "en",
  onApplyTitle,
  onApplyBody,
  onApplyTags,
  defaultOpen = true,
  testIdPrefix = "ai-assistant",
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null); // { intent, items: [...] }
  const [error, setError] = useState(null);

  const request = useCallback(async (intent) => {
    setBusy(intent);
    setError(null);
    setResult(null);
    try {
      const body = {
        intent,
        topic: topic || null,
        category: category || null,
        location: location || null,
        current_title: currentTitle || null,
        current_body: currentBody || null,
        surface,
        language,
        count: 5,
      };
      const r = await fetch(`${API}/ai/assist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      const data = await r.json();
      const raw = data.result || {};
      let items = [];
      if (intent === "suggest_titles")  items = raw.titles    || [];
      if (intent === "make_question")   items = raw.questions || [];
      if (intent === "starter_prompts") items = raw.starters  || [];
      if (intent === "suggest_tags")    items = raw.tags      || [];
      if (intent === "improve_draft")   items = raw.improved  ? [raw.improved]  : [];
      if (intent === "expand_draft")    items = raw.expanded  ? [raw.expanded]  : [];
      setResult({ intent, items });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  }, [topic, category, location, currentTitle, currentBody, surface, language]);

  const applyItem = useCallback((item) => {
    if (!result) return;
    const { intent } = result;
    if (["suggest_titles", "make_question", "starter_prompts"].includes(intent)) {
      onApplyTitle?.(item);
    } else if (intent === "improve_draft" || intent === "expand_draft") {
      onApplyBody?.(item);
    } else if (intent === "suggest_tags") {
      onApplyTags?.(item);
    }
  }, [result, onApplyTitle, onApplyBody, onApplyTags]);

  const activeIntentMeta = useMemo(
    () => INTENTS.find((i) => i.key === result?.intent),
    [result],
  );

  return (
    <div
      className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-purple-50 shadow-sm overflow-hidden"
      data-testid={testIdPrefix}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-white/40"
        data-testid={`${testIdPrefix}-toggle`}
      >
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 text-white inline-flex items-center justify-center shadow-sm">
            <Sparkles className="w-4 h-4" />
          </span>
          <div className="text-left">
            <div className="text-sm font-bold text-slate-900">AI Assistant</div>
            <div className="text-[11px] text-slate-500">Stuck? Get titles, tags, ideas, and polish.</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
      </button>

      {open && (
        <div className="px-4 pb-4">
          <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-chips`}>
            {INTENTS.map((intent) => {
              const Icon = intent.icon;
              const isBusy = busy === intent.key;
              return (
                <button
                  key={intent.key}
                  type="button"
                  onClick={() => request(intent.key)}
                  disabled={!!busy}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    result?.intent === intent.key
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:text-blue-700"
                  } disabled:opacity-50`}
                  title={intent.hint}
                  data-testid={`${testIdPrefix}-chip-${intent.key}`}
                >
                  {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
                  {intent.label}
                </button>
              );
            })}
          </div>

          {error && (
            <div className="mt-3 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-700 flex items-start justify-between gap-2" data-testid={`${testIdPrefix}-error`}>
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)} className="text-red-500 hover:text-red-700"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}

          {result && !error && (
            <div className="mt-3 space-y-2" data-testid={`${testIdPrefix}-results`}>
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">
                {activeIntentMeta?.label} · click to apply
              </div>
              {result.items.length === 0 ? (
                <p className="text-xs text-slate-500">The AI returned no suggestions — try again with more context.</p>
              ) : result.intent === "suggest_tags" ? (
                <div className="flex flex-wrap gap-2">
                  {result.items.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => applyItem(tag)}
                      className="text-xs px-2.5 py-1 rounded-full bg-white border border-slate-200 hover:border-blue-400 hover:text-blue-700"
                      data-testid={`${testIdPrefix}-tag-${tag}`}
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {result.items.map((item, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => applyItem(item)}
                      className="w-full text-left px-3 py-2 rounded-lg bg-white border border-slate-200 hover:border-blue-400 hover:shadow-sm text-sm text-slate-800"
                      data-testid={`${testIdPrefix}-item-${idx}`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
